import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Home, Unlink, Search, X, AlertTriangle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { upsertFamilyGroup, dissolveFamilyGroup, removeFamilyMember, setFamilyView } from '../../../lib/api';

const isAdminRole = (u) => ['admin', 'admin_primary', 'admin_secondary'].includes(u?.role);

const UserLine = ({ u }) => (
  <div className="min-w-0 flex-1">
    <p className="text-sm text-white truncate flex items-center gap-1">
      {u.name}
      {isAdminRole(u) && <Shield size={11} className="ht-warn" title="Admin" />}
    </p>
    <p className="text-[11px] text-disabled truncate">{u.email}</p>
  </div>
);

export const FamilyGroupModal = ({ open, onOpenChange, targetUser, allUsers = [], onChanged }) => {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type:'remove'|'dissolve', member}
  const [toggling, setToggling] = useState(null);

  const groupId = targetUser?.family_group_id || null;
  const byId = useMemo(() => Object.fromEntries(allUsers.map(u => [u.id, u])), [allUsers]);
  const members = useMemo(
    () => (groupId ? allUsers.filter(u => u.family_group_id === groupId).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it')) : []),
    [allUsers, groupId]
  );

  useEffect(() => {
    if (!open || !targetUser) return;
    setSelected(members.length ? members.map(m => m.id) : [targetUser.id]);
    setSearch('');
  }, [open, targetUser, members]);

  const selectedUsers = selected.map(id => byId[id]).filter(Boolean);
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allUsers
      .filter(u => !selected.includes(u.id))
      .filter(u => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, allUsers, selected]);
  const inOtherFamily = (u) => u?.family_group_id && u.family_group_id !== groupId;

  const handleSave = async () => {
    if (selected.length < 2) return toast.error('Seleziona almeno 2 membri');
    setSaving(true);
    try {
      await upsertFamilyGroup(selected, groupId);
      toast.success(groupId ? 'Famiglia aggiornata' : 'Famiglia creata');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleView = async (m, enabled) => {
    setToggling(m.id);
    try {
      await setFamilyView(groupId, m.id, enabled);
      toast.success(enabled ? `${m.name} ora vede i risultati della famiglia` : `Vista famiglia spenta per ${m.name}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setToggling(null);
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setSaving(true);
    try {
      if (confirm.type === 'dissolve') {
        await dissolveFamilyGroup(groupId);
        toast.success('Famiglia sciolta');
      } else {
        const res = await removeFamilyMember(groupId, confirm.member.id);
        toast.success(res.data.dissolved ? 'Famiglia sciolta (restava un solo membro)' : `${confirm.member.name} rimosso dalla famiglia`);
      }
      setConfirm(null);
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setSaving(false);
    }
  };

  if (!targetUser) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#111827] border-[#282c34] text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="family-group-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Home size={18} className="text-sky-400" /> Famiglia</DialogTitle>
          </DialogHeader>

          {members.length > 0 && (
            <div className="bg-[#11161d]/40 rounded-lg p-3 space-y-2" data-testid="family-group-current">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-disabled">Membri ({members.length})</p>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400 hover:bg-red-500/10" data-testid="dissolve-family-btn"
                  onClick={() => setConfirm({ type: 'dissolve' })}>
                  <Unlink size={12} className="mr-1" /> Sciogli famiglia
                </Button>
              </div>
              <p className="text-[11px] text-disabled">Interruttore "Vede i risultati della famiglia": spegni quando l'utente diventa autonomo.</p>
              {members.map(m => (
                <div key={m.id} className="flex items-center gap-2 py-1.5 border-t border-[#282c34]/60" data-testid={`family-member-${m.id}`}>
                  <UserLine u={m} />
                  {!isAdminRole(m) ? (
                    <label className="flex items-center gap-1.5 text-[11px] text-gray-400" title="Vede i risultati della famiglia">
                      <Switch
                        checked={!!m.family_view}
                        disabled={toggling === m.id}
                        onCheckedChange={(v) => handleToggleView(m, v)}
                        data-testid={`family-view-switch-${m.id}`}
                      />
                      <span className="hidden sm:inline">Vede i risultati</span>
                    </label>
                  ) : (
                    <span className="text-[10px] ht-warn/80">admin</span>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-400 hover:text-red-400" data-testid={`remove-family-member-btn-${m.id}`}
                    onClick={() => setConfirm({ type: 'remove', member: m })}>
                    Rimuovi
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-disabled">{groupId ? 'Modifica membri' : 'Nuova famiglia'}</p>
            <div className="space-y-1" data-testid="family-group-selected">
              {selectedUsers.map(u => (
                <div key={u.id} className="flex items-center gap-2 rounded-lg p-2 border border-[#282c34] bg-[#0d1117]/40">
                  <UserLine u={u} />
                  {inOtherFamily(u) && <span className="text-[10px] text-red-400 flex items-center gap-1"><AlertTriangle size={11} /> già in altra famiglia</span>}
                  <button onClick={() => setSelected(prev => prev.filter(x => x !== u.id))} className="text-gray-500 hover:text-white" data-testid={`remove-selected-family-btn-${u.id}`}><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-disabled" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca utente per nome o email…"
                className="pl-8 ht-panel text-white h-9" data-testid="family-group-search-input" />
            </div>
            {candidates.length > 0 && (
              <div className="space-y-1" data-testid="family-group-search-results">
                {candidates.map(u => (
                  <button key={u.id} onClick={() => { setSelected(prev => [...prev, u.id]); setSearch(''); }} data-testid={`family-search-result-btn-${u.id}`}
                    className="w-full flex items-center gap-2 rounded-lg p-2 border border-[#282c34] hover:bg-[#11161d]/60 text-left">
                    <UserLine u={u} />
                    {inOtherFamily(u) && <span className="text-[10px] text-red-400 flex items-center gap-1"><AlertTriangle size={11} /> altra famiglia</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#282c34] text-gray-300">Annulla</Button>
            <Button onClick={handleSave} disabled={saving || selected.length < 2} className="bg-sky-600 hover:bg-sky-700" data-testid="family-group-save-btn">
              {saving ? 'Salvo…' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="ht-panel" data-testid="family-group-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {confirm?.type === 'dissolve' ? 'Sciogliere la famiglia?' : `Rimuovere ${confirm?.member?.name} dalla famiglia?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {confirm?.type === 'dissolve'
                ? 'Tutti i membri verranno scollegati e il riepilogo famiglia sarà disattivato subito per ognuno.'
                : members.length <= 2
                  ? 'Resterebbe un solo membro: la famiglia verrà sciolta completamente.'
                  : "L'utente non vedrà più il riepilogo famiglia, con effetto immediato."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#11161d] text-white border-[#282c34]">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={saving} className="ht-btn ht-btn-danger" data-testid="family-group-confirm-btn">
              {saving ? 'Attendi…' : 'Conferma'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default FamilyGroupModal;
